import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { Form, Button, Card, Row, Col, Badge, Accordion } from 'react-bootstrap';
import { CheckCircle2, Save, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAlert } from '../../context/AlertContext';

interface Professional {
    id: number;
    name: string;
}

interface Agenda {
    id: number;
    professional: number;
    name: string;
}

interface Props {
    onBack: () => void;
    onComplete: () => void;
}

export const AgendaStep: React.FC<Props> = ({ onBack, onComplete }) => {
    const navigate = useNavigate();
    const [professionals, setProfessionals] = useState<Professional[]>([]);
    const [agendas, setAgendas] = useState<Agenda[]>([]);
    const [fetching, setFetching] = useState(true);
    const [loadingForms, setLoadingForms] = useState<Record<number, boolean>>({});
    const { showAlert } = useAlert();

    const [formStates, setFormStates] = useState<Record<number, any>>({});

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [profsRes, agendasRes] = await Promise.all([
                api.get('/professionals/'),
                api.get('/agendas/')
            ]);
            setProfessionals(profsRes.data);
            setAgendas(agendasRes.data);

            // Initialize form states for professionals who don't have an agenda yet
            const currentAgendas = agendasRes.data;
            const initialFormStates: Record<number, any> = {};

            profsRes.data.forEach((prof: Professional) => {
                const hasAgenda = currentAgendas.some((a: Agenda) => a.professional === prof.id);
                if (!hasAgenda) {
                    initialFormStates[prof.id] = {
                        name: `Agenda de ${prof.name}`,
                        start_time: '09:00:00',
                        end_time: '18:00:00',
                        slot_duration: '00:30:00',
                        open_ended: true,
                        monday: true,
                        tuesday: true,
                        wednesday: true,
                        thursday: true,
                        friday: true,
                        saturday: false,
                        sunday: false,
                    };
                }
            });
            setFormStates(initialFormStates);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setFetching(false);
        }
    };

    const handleFormChange = (profId: number, field: string, value: any) => {
        setFormStates(prev => ({
            ...prev,
            [profId]: {
                ...prev[profId],
                [field]: value
            }
        }));
    };

    const handleCreateAgenda = async (profId: number) => {
        setLoadingForms(prev => ({ ...prev, [profId]: true }));
        try {
            const dataToSubmit = {
                ...formStates[profId],
                professional: profId
            };
            await api.post('/agendas/', dataToSubmit);
            await fetchData(); // Refetch to update agendas list
            showAlert('Agenda salva com sucesso!', { type: 'success' });
        } catch (error) {
            console.error('Error creating agenda:', error);
            showAlert('Erro ao salvar agenda. Verifique os dados e tente novamente.', { type: 'error' });
        } finally {
            setLoadingForms(prev => ({ ...prev, [profId]: false }));
        }
    };

    const handleFinish = () => {
        // Redirecionar para o painel principal após completar
        navigate('/scheduling');
    };

    if (fetching) {
        return <div className="text-center p-5">Carregando...</div>;
    }

    const isReadyToProceed = professionals.length > 0 && professionals.every(prof =>
        agendas.some(a => a.professional === prof.id)
    );

    return (
        <div>
            <div className="step-header">
                <h3>Horários de Atendimento</h3>
                <p className="text-muted">Configure o horário de trabalho de cada profissional para que os clientes possam agendar.</p>
            </div>

            <Accordion defaultActiveKey={professionals.map(p => p.id.toString())} alwaysOpen className="mb-4">
                {professionals.map(prof => {
                    const profAgendas = agendas.filter(a => a.professional === prof.id);
                    const hasAgenda = profAgendas.length > 0;
                    const formData = formStates[prof.id];

                    return (
                        <Accordion.Item eventKey={prof.id.toString()} key={prof.id} className="mb-3 border rounded shadow-sm">
                            <Accordion.Header>
                                <div className="d-flex align-items-center w-100 me-3">
                                    <h6 className="mb-0 fw-bold me-auto">{prof.name}</h6>
                                    {hasAgenda ? (
                                        <Badge bg="success" className="d-flex align-items-center"><CheckCircle2 size={14} className="me-1" /> Configurado</Badge>
                                    ) : (
                                        <Badge bg="warning" text="dark">Pendente</Badge>
                                    )}
                                </div>
                            </Accordion.Header>
                            <Accordion.Body className="bg-light">
                                {hasAgenda ? (
                                    <div className="text-success d-flex align-items-center p-3 bg-success-subtle rounded border border-success-subtle">
                                        <CheckCircle2 size={24} className="me-3" />
                                        <div>
                                            <strong>Agenda configurada com sucesso!</strong>
                                            <p className="mb-0 small text-dark">Você pode editar os detalhes avançados da agenda na seção "Configurações" depois.</p>
                                        </div>
                                    </div>
                                ) : (
                                    formData && (
                                        <div className="p-3 bg-white rounded border">
                                            <Row>
                                                <Col xs={12} md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Nome da Configuração</Form.Label>
                                                        <Form.Control
                                                            value={formData.name}
                                                            onChange={(e) => handleFormChange(prof.id, 'name', e.target.value)}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col xs={12} md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Duração Padrão do Atendimento</Form.Label>
                                                        <Form.Select
                                                            value={formData.slot_duration}
                                                            onChange={(e) => handleFormChange(prof.id, 'slot_duration', e.target.value)}
                                                        >
                                                            <option value="00:15:00">15 minutos</option>
                                                            <option value="00:30:00">30 minutos</option>
                                                            <option value="00:45:00">45 minutos</option>
                                                            <option value="01:00:00">1 hora</option>
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col xs={6} md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Horário de Início</Form.Label>
                                                        <Form.Control
                                                            type="time"
                                                            value={formData.start_time}
                                                            onChange={(e) => handleFormChange(prof.id, 'start_time', e.target.value)}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col xs={6} md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Horário de Fim</Form.Label>
                                                        <Form.Control
                                                            type="time"
                                                            value={formData.end_time}
                                                            onChange={(e) => handleFormChange(prof.id, 'end_time', e.target.value)}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>

                                            <Form.Group className="mb-4">
                                                <Form.Label className="fw-bold mb-3 d-block">Dias de Trabalho</Form.Label>
                                                <div className="weekday-selector">
                                                    {[
                                                        { id: 'sunday', short: 'Dom', full: 'Domingo' },
                                                        { id: 'monday', short: 'Seg', full: 'Segunda-feira' },
                                                        { id: 'tuesday', short: 'Ter', full: 'Terça-feira' },
                                                        { id: 'wednesday', short: 'Qua', full: 'Quarta-feira' },
                                                        { id: 'thursday', short: 'Qui', full: 'Quinta-feira' },
                                                        { id: 'friday', short: 'Sex', full: 'Sexta-feira' },
                                                        { id: 'saturday', short: 'Sáb', full: 'Sábado' },
                                                    ].map(day => (
                                                        <div 
                                                            key={day.id} 
                                                            className={`weekday-item ${formData[day.id] ? 'active' : ''}`} 
                                                            onClick={() => handleFormChange(prof.id, day.id, !formData[day.id])}
                                                        >
                                                            {formData[day.id] && <span className="weekday-check">✓</span>}
                                                            <span className="weekday-label-short">{day.short}</span>
                                                            <span className="weekday-label-full">{day.full}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </Form.Group>

                                            <div className="d-grid d-md-block">
                                                <Button
                                                    variant="primary"
                                                    onClick={() => handleCreateAgenda(prof.id)}
                                                    disabled={loadingForms[prof.id]}
                                                    className="d-flex align-items-center justify-content-center ms-auto gap-2"
                                                >
                                                    <Save size={18} />
                                                    {loadingForms[prof.id] ? 'Salvando...' : 'Salvar Agenda'}
                                                </Button>
                                            </div>
                                        </div>
                                    )
                                )}
                            </Accordion.Body>
                        </Accordion.Item>
                    );
                })}
            </Accordion>

            <div className="step-navigation">
                {isReadyToProceed ? (
                    <div className="step-navigation-status text-success fw-bold">
                        <CheckCircle2 size={20} /> Tudo pronto!
                    </div>
                ) : (
                    <div className="step-navigation-status text-muted">
                        Configure a agenda de todos os profissionais ({agendas.length}/{professionals.length})
                    </div>
                )}
                <div className="step-navigation-buttons">
                    <Button variant="outline-secondary" onClick={onBack} className="btn-step-back">
                        <ArrowLeft size={18} /> Voltar
                    </Button>
                    <Button
                        variant="success"
                        size="lg"
                        onClick={handleFinish}
                        disabled={!isReadyToProceed}
                        className="btn-step-next px-5 shadow-sm"
                    >
                        Finalizar Configuração
                    </Button>
                </div>
            </div>
        </div>
    );
};
